import { Component, computed, forwardRef, inject, Input, signal } from '@angular/core';
import {
  TenantItemDto,
  TenantMergeDto,
  TenantService,
} from '@artemis-software/wr-api';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { Router } from '@angular/router';
import { NbDialogService } from '@nebular/theme';
import TenantTypeEnum = TenantMergeDto.TenantTypeEnum;

@Component({
  standalone: false,
  selector: 'wr-tenant-pick-list',
  templateUrl: './tenant-pick-list.component.html',
  styleUrls: ['./tenant-pick-list.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TenantPickListComponent),
      multi: true,
    },
  ],
})
export class TenantPickListComponent extends AbstractFormControl<TenantItemDto[]> {

  @Input()
  tenantType: TenantTypeEnum = TenantTypeEnum.Internal;

  readonly tenantService = inject(TenantService);
  readonly router = inject(Router);
  readonly nbDialogService = inject(NbDialogService);

  readonly tenants = asyncComputed(() => this.tenantService.findAll({ type: this.tenantType }));

  searchText = signal<string>('');

  reload = signal<number>(0);

  readonly filteredCheckboxTenants = computed(() => {
    this.reload();
    const searchText = this.searchText().toLowerCase();
    const tenants = this.tenants();
    if (tenants) {
      return tenants.filter(it => it.name.includes(searchText)).sort(
        (a, b) => {
          const aIsSelected = this.value?.some(it => it.id === a.id) ? -1 : 1;
          const bIsSelected = this.value?.some(it => it.id === b.id) ? -1 : 1;
          if (aIsSelected !== bIsSelected) {
            return aIsSelected - bIsSelected;
          }
          return a.name.localeCompare(b.name);
        },
      );
    }
    return [];
  });

  readonly filteredAssignedTenants = computed(() => {
    this.reload();
    return this.value?.filter(it => (it.name).includes(this.searchText())).sort(
      (a, b) => a.name.localeCompare(b.name)
    );
  });

  searchTextChange($event: any) {
    this.searchText.set($event);
  }

  constructor() {
    super();
  }

  toggle(checked: boolean, tenant: TenantItemDto): void {
    if (checked) {
      this.value?.push(tenant);
    } else {
      this.value = this.value?.filter(it => it.id !== tenant.id);
    }
    this.reload.set(this.reload() + 1);
  }

  isChecked(tenant: TenantItemDto): boolean {
    return this.value?.some(it => it.id === tenant.id) ?? false;
  }

  override writeValue(tenants: TenantItemDto[]): void {
    super.writeValue(tenants);
  }

  goToTenant(event: Event, tenant: TenantItemDto): void {
    event.stopPropagation();
    this.router.navigate(['tenant', tenant.id]);
  }
}
